import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig, getJobPortalSession } from "../../../helpers/jobPortalAuth";

const WORK_MODE_COLORS = {
  remote: { color: "#16803c", bg: "#eaf8ef" },
  onsite: { color: "#1d72fa", bg: "#eef4ff" },
  hybrid: { color: "#c96a07", bg: "#fff5e8" },
};

const formatSalary = (value) => {
  if (!value || value === 0) return null;
  if (value >= 100000) return `Rs ${Number(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `Rs ${Number(value / 1000).toFixed(0)}K`;
  return `Rs ${value}`;
};

const getInitials = (value) =>
  String(value || "CO")
    .split(" ")
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const SkeletonCard = () => (
  <div className="col-xl-6">
    <div className="saved-theme-card placeholder-glow">
      <div className="d-flex gap-3">
        <span className="placeholder rounded-4" style={{ width: 56, height: 56 }} />
        <div className="flex-grow-1">
          <span className="placeholder col-6 rounded d-block mb-2" />
          <span className="placeholder col-4 rounded d-block mb-3" />
          <span className="placeholder col-10 rounded d-block" />
        </div>
      </div>
    </div>
  </div>
);

const SeekerSavedJobs = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [appliedJobIds, setAppliedJobIds] = useState(new Set());

  const fetchSavedJobs = async () => {
    try {
      setLoading(true);
      await getJobPortalSession();
      const authConfig = await getJobPortalAuthConfig();
      const [savedJobsResponse, applicationsResponse] = await Promise.all([
        axiosInstance.get("/job-portal/jobs/saved", authConfig),
        axiosInstance.get("/job-portal/applications/me?limit=100", authConfig),
      ]);

      setJobs(savedJobsResponse.data?.data?.jobs || []);
      setAppliedJobIds(
        new Set((applicationsResponse.data?.data?.applications || []).map((item) => item.jobId))
      );
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load saved jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedJobs();
  }, []);

  const removeSavedJob = async (jobId) => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.delete(`/job-portal/jobs/${jobId}/save`, authConfig);
      toast.success("Job removed from saved list");
      setJobs((prev) => prev.filter((job) => job.id !== jobId));
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to remove saved job");
    }
  };

  const applyToJob = async (jobId) => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.post(`/job-portal/jobs/${jobId}/apply`, {}, authConfig);
      toast.success("Application submitted");
      setAppliedJobIds((prev) => new Set(prev).add(jobId));
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to apply for job");
    }
  };

  return (
    <>
      <style>{`
        .saved-theme-shell {
          background: linear-gradient(180deg, #f4f7fb 0%, #eef2f8 100%);
          min-height: 100vh;
          padding-bottom: 2rem;
        }
        .saved-theme-hero {
          background: #ffffff;
          border: 1px solid #e7ebf3;
          border-radius: 28px;
          box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
          overflow: hidden;
        }
        .saved-theme-band {
          background: linear-gradient(135deg, #1d72fa 0%, #1664e8 100%);
          color: #fff;
          padding: 1.5rem;
          position: relative;
        }
        .saved-theme-band::after {
          background:
            linear-gradient(135deg, transparent 55%, #09111f 55%, #09111f 67%, transparent 67%),
            linear-gradient(135deg, transparent 20%, #6bc6ff 20%, #6bc6ff 34%, transparent 34%),
            linear-gradient(135deg, transparent 72%, #ffffff 72%, #ffffff 81%, transparent 81%);
          content: "";
          inset: 0;
          opacity: 0.9;
          position: absolute;
        }
        .saved-theme-band > * { position: relative; z-index: 1; }
        .saved-theme-toolbar {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 0.85rem;
          justify-content: space-between;
          padding: 1rem 1.25rem;
        }
        .saved-theme-count {
          background: #eef4ff;
          border: 1px solid #dbe7fb;
          border-radius: 999px;
          color: #1d72fa;
          display: inline-flex;
          font-size: 0.82rem;
          font-weight: 700;
          gap: 0.5rem;
          padding: 0.55rem 0.95rem;
        }
        .saved-theme-card {
          background: #fff;
          border: 1px solid #e7ebf3;
          border-radius: 24px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          height: 100%;
          padding: 1.25rem;
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .saved-theme-card:hover {
          border-color: #bfd6ff;
          box-shadow: 0 20px 40px rgba(29, 114, 250, 0.1);
          transform: translateY(-2px);
        }
        .saved-theme-logo {
          align-items: center;
          background: linear-gradient(180deg, #eef4ff 0%, #dce9ff 100%);
          border-radius: 16px;
          color: #1d72fa;
          display: inline-flex;
          font-size: 1rem;
          font-weight: 800;
          height: 56px;
          justify-content: center;
          min-width: 56px;
        }
        .saved-theme-title {
          color: #10151c;
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          margin-bottom: 0.25rem;
        }
        .saved-theme-meta {
          color: #6c7385;
          font-size: 0.84rem;
        }
        .saved-theme-salary {
          color: #16803c;
          font-size: 0.92rem;
          font-weight: 700;
        }
        .saved-theme-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .saved-theme-chip {
          background: #f3f5f9;
          border-radius: 999px;
          color: #556070;
          font-size: 0.76rem;
          font-weight: 600;
          padding: 0.4rem 0.75rem;
        }
        .saved-theme-footer {
          align-items: center;
          border-top: 1px solid #eef2f7;
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          justify-content: space-between;
          padding-top: 1rem;
        }
        .saved-theme-actions {
          display: flex;
          gap: 0.75rem;
        }
        .saved-empty-card {
          background: #fff;
          border: 1px solid #e7ebf3;
          border-radius: 24px;
          box-shadow: 0 14px 32px rgba(15, 23, 42, 0.05);
          padding: 3rem 1.5rem;
          text-align: center;
        }
        @media (max-width: 767px) {
          .saved-theme-toolbar,
          .saved-theme-footer,
          .saved-theme-actions {
            align-items: stretch;
            flex-direction: column;
          }
          .saved-theme-actions .btn,
          .saved-theme-toolbar .btn {
            width: 100%;
          }
        }
      `}</style>

      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="saved-theme-shell">
          <div className="container-fluid py-4">
            <div className="saved-theme-hero mb-4">
              <div className="saved-theme-band">
                <div className="text-uppercase fw-bold small mb-2" style={{ letterSpacing: "0.08em" }}>
                  Saved opportunities
                </div>
                <h2 className="mb-2 fw-bold">Keep your best-fit roles in one place</h2>
                <p className="mb-0" style={{ maxWidth: 580, color: "rgba(255,255,255,0.82)" }}>
                  Review shortlisted roles, compare company details, and apply when you are ready.
                </p>
              </div>

              <div className="saved-theme-toolbar">
                <div>
                  {!loading ? (
                    <span className="saved-theme-count">
                      <i className="bi bi-bookmark-heart-fill" />
                      {jobs.length} saved jobs
                    </span>
                  ) : null}
                </div>
                <Link to="/job-seeker/jobs" className="btn btn-primary">
                  <i className="bi bi-search me-2" />
                  Browse More Jobs
                </Link>
              </div>
            </div>

            {loading ? (
              <div className="row g-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            ) : jobs.length === 0 ? (
              <div className="saved-empty-card">
                <i className="bi bi-bookmark text-muted" style={{ fontSize: 52, opacity: 0.25 }} />
                <h4 className="mt-3 mb-2">No saved jobs yet</h4>
                <p className="text-muted mb-4">
                  Bookmark roles while browsing and come back when you want to apply.
                </p>
                <Link to="/job-seeker/jobs" className="btn btn-primary">
                  Explore Jobs
                </Link>
              </div>
            ) : (
              <div className="row g-4">
                {jobs.map((job) => {
                  const isApplied = appliedJobIds.has(job.id);
                  const salaryMin = formatSalary(job.salary?.minimum);
                  const salaryMax = formatSalary(job.salary?.maximum);
                  const workModeStyle = WORK_MODE_COLORS[job.workMode] || {
                    color: "#556070",
                    bg: "#f3f5f9",
                  };

                  return (
                    <div className="col-xl-6" key={job.id}>
                      <div className="saved-theme-card">
                        <div className="d-flex gap-3 align-items-start">
                          <span className="saved-theme-logo">
                            {getInitials(job.company?.name || job.title)}
                          </span>
                          <div className="flex-grow-1">
                            <div className="saved-theme-title">{job.title}</div>
                            <div className="saved-theme-meta">
                              <i className="bi bi-building me-1" />
                              {job.company?.name || "Company"}
                              <span className="mx-2">·</span>
                              <i className="bi bi-geo-alt me-1" />
                              {job.location?.display || "Location TBD"}
                            </div>
                          </div>
                        </div>

                        {salaryMin || salaryMax ? (
                          <div className="saved-theme-salary">
                            {salaryMin && salaryMax ? `${salaryMin} - ${salaryMax}` : salaryMin || salaryMax}
                            <span className="text-muted fw-normal ms-2">per year</span>
                          </div>
                        ) : null}

                        <div className="saved-theme-chips">
                          {job.workMode ? (
                            <span
                              className="saved-theme-chip"
                              style={{ background: workModeStyle.bg, color: workModeStyle.color }}
                            >
                              {job.workMode.replace(/_/g, " ")}
                            </span>
                          ) : null}
                          {job.employmentType ? (
                            <span className="saved-theme-chip">
                              {job.employmentType.replace(/_/g, " ")}
                            </span>
                          ) : null}
                          {(job.skills || []).slice(0, 3).map((skill) => (
                            <span className="saved-theme-chip" key={skill}>
                              {skill}
                            </span>
                          ))}
                        </div>

                        <div className="saved-theme-footer">
                          <div className="text-muted small">
                            {job.experience?.minimum ?? 0}-{job.experience?.maximum ?? 0} years experience
                          </div>
                          <div className="saved-theme-actions">
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => removeSavedJob(job.id)}
                            >
                              Remove
                            </button>
                            <button
                              className={`btn ${isApplied ? "btn-outline-secondary" : "btn-primary"}`}
                              disabled={isApplied}
                              onClick={() => applyToJob(job.id)}
                            >
                              {isApplied ? "Applied" : "Apply Now"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default SeekerSavedJobs;
